import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ChatState {
    isOpen: boolean;
    isCareRoomOpen: boolean;
    proxyChatRoomId: number;
    proxyCareRoomId: number;
}

interface ChatContextType {
    chat: ChatState;
    openFloatingChatroom: (payload?: { chatRoomId?: number }) => void;
    closeFloatingChatroom: () => void;
    resetProxyChatRoomId: () => void;
    openFloatingCareroom: (payload?: { careRoomId?: number }) => void;
    closeFloatingCareroom: () => void;
    resetProxyCareRoomId: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const initialChatState: ChatState = {
    isOpen: false,
    isCareRoomOpen: false,
    proxyChatRoomId: -1,
    proxyCareRoomId: -1,
};

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [chat, setChat] = useState<ChatState>(initialChatState);

    const openFloatingCareroom = (payload?: { careRoomId?: number }) => {
        setChat(prev => ({
            ...prev,
            isOpen: false,
            proxyChatRoomId: -1,
            isCareRoomOpen: true,
            proxyCareRoomId: payload?.careRoomId ?? -1,
        }));
    };

    const closeFloatingCareroom = () => {
        setChat(prev => ({
            ...prev,
            isCareRoomOpen: false,
            proxyCareRoomId: -1,
        }));
    };

    const resetProxyCareRoomId = () => {
        setChat(prev => ({
            ...prev,
            proxyCareRoomId: -1,
        }));
    };

    const openFloatingChatroom = (payload?: { chatRoomId?: number }) => {
        setChat(prev => ({
            ...prev,
            isCareRoomOpen: false,
            proxyCareRoomId: -1,
            isOpen: true,
            proxyChatRoomId: payload?.chatRoomId ?? -1,
        }));
    };

    const closeFloatingChatroom = () => {
        setChat(prev => ({
            ...prev,
            isOpen: false,
            proxyChatRoomId: -1,
        }));
    };

    const resetProxyChatRoomId = () => {
        setChat(prev => ({
            ...prev,
            proxyChatRoomId: -1,
        }));
    };

    return (
        <ChatContext.Provider
            value={{
                chat,
                openFloatingChatroom,
                closeFloatingChatroom,
                resetProxyChatRoomId,
                openFloatingCareroom,
                closeFloatingCareroom,
                resetProxyCareRoomId,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within ChatProvider');
    }
    return context;
};
